import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
  Chip,
  OutlinedInput,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const ShopEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate("shop/shop", token);
  const auth = useAuth();
  const currentUser = auth?.user?.user || auth?.user;
  const isSuperAdmin = Boolean(currentUser?.is_superuser);

  const { data: usersData, isLoading: isUsersLoading } = useFetchObjects(
    ["shop-assigned-users"],
    "admin/users/?page_size=200",
    token,
    { enabled: isSuperAdmin && open }
  );

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [assignedUsers, setAssignedUsers] = useState([]);

  const users = usersData?.results || usersData || [];

  useEffect(() => {
    if (editData) {
      setName(editData.name || "");
      setDescription(editData.description || "");
      setAssignedUsers(editData.assigned_users || []);
    }
  }, [editData]);

  const handleSubmit = () => {
    const formData = {
      name: name.trim(),
      description,
    };
    if (isSuperAdmin) {
      formData.assigned_users = assignedUsers;
    }
    handleUpdate(editData.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      setName("");
      setDescription("");
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("shop.editShop")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {isSuperAdmin && (
            <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
              <InputLabel id="assigned-users-label">
                {t("shop.assignedUsers", "Assigned Users")}
              </InputLabel>
              <Select
                labelId="assigned-users-label"
                multiple
                value={assignedUsers}
                onChange={(e) => setAssignedUsers(e.target.value)}
                disabled={isUsersLoading}
                input={
                  <OutlinedInput
                    label={t("shop.assignedUsers", "Assigned Users")}
                  />
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => {
                      const user = users.find((u) => u.id === value);
                      return (
                        <Chip
                          key={value}
                          label={
                            user?.username || user?.email || `User ${value}`
                          }
                        />
                      );
                    })}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.username || user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={t("shop.shopName")}
            type="text"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("shop.description")}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("shop.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !name.trim()}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("shop.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShopEdit;
